import * as i0 from '@angular/core';
import { InjectionToken, inject, InjectFlags, Injectable, Optional, Inject } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpContextToken, HttpStatusCode, HttpContext, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BehaviorSubject, Subject, throwError, combineLatest } from 'rxjs';
import { tap, catchError, filter, take, switchMap, map } from 'rxjs/operators';

/**
 * This service provided by this token, needs to handle the case when refresh is failed, and further requests are impossible.
 * It can be logical to make a logout.
 * In other words, the service (that was provided by this token) should implement "logout" method.
 * */
const AUTH_ANGULAR__LOGOUT_SERVICE = new InjectionToken('service_that_handles_401');
/**
 * The config that allows to customize some consts for making requests.
 * It includes name of the Header that should be used to attach token.
 * Some prefix like "Bearer " that indicates the type of authorization
 * The Path of the url that will be used to refresh token.
 * The maximum count of attempts one particular request.
 * (Can be helpful in case when authentication-ms responses with success status code but token is not valid itself, In that case it is required to break infinite loop of refreshing token).
 * */
const AUTH_ANGULAR__INTERCEPT_CUSTOM_CONFIG = new InjectionToken('auth_intercept_config_to_customize default');
/**
 * The app id that will be used to refresh token it is passed like queryParam "appId" in the refresh request
 * */
const AUTH_ANGULAR__APP_ID = new InjectionToken('the_name_of_the_app_will_be_ues_to_refresh_token');

const defaultConfig = {
    tokenHeaderName: 'Authorization',
    prefixToken: 'Bearer ',
    countOfRefreshRetries: 3,
    refreshPath: 'api/v1/auth/session/refresh'
};

const AUTH_ANGULAR__CONFIG = new InjectionToken('inner intercept config', {
    providedIn: 'root',
    factory() {
        const customConfig = inject(AUTH_ANGULAR__INTERCEPT_CUSTOM_CONFIG, InjectFlags.Optional);
        return customConfig ? { ...defaultConfig, ...customConfig } : defaultConfig;
    }
});

/**
 * This Context token allows to skip JWT adding to particular request in auth-angular library
 * */
const AUTH_ANGULAR__SKIP_JWT_ADDING = new HttpContextToken(() => false);
/**
 * This Context token allows to skip adding context header to particular request
 * */
const REQUEST_CONTEXT__SKIP_ADDING_HEADER = new HttpContextToken(() => false);

const getInitialState = () => {
    return {
        sessionId: '',
        accessToken: '',
        authUrl: '',
        sessionType: '',
    };
};
class AuthorizationInterceptorService {
    constructor(http, logoutService, appId, config) {
        this.http = http;
        this.logoutService = logoutService;
        this.appId = appId;
        this.config = config;
        this.jwtState$ = new BehaviorSubject("AUTH_ANGULAR__Ready" /* JWTStateEnum.Ready */);
        this.loginRequiredSubject$ = new Subject();
        this.authInfoSubject = new BehaviorSubject(getInitialState());
        this.authInfo$ = this.authInfoSubject.pipe();
    }
    get loginRequired$() {
        return this.loginRequiredSubject$.asObservable();
    }
    get jwtState() {
        return this.jwtState$.getValue();
    }
    get authInfo() {
        return this.authInfoSubject.getValue();
    }
    get jwt() {
        return this.authInfoSubject.getValue()?.accessToken;
    }
    get isJWTSupported() {
        return !!this.jwt;
    }
    refreshAuthInfo() {
        const authInfo = this.authInfo;
        return this.refreshToken(authInfo.accessToken, authInfo.sessionId, authInfo.authUrl)
            .pipe(tap(({ sessionId, accessToken }) => {
            const valueToUpdate = { ...this.authInfo, sessionId, accessToken };
            this.updateAuthInfo(valueToUpdate);
        }), catchError((error) => {
            if (error.status === HttpStatusCode.Unauthorized) {
                this.backToLogin();
            }
            return throwError(() => error);
        }));
    }
    backToLogin() {
        this.logoutService?.logout(this.authInfo);
        this.loginRequiredSubject$.next(this.authInfo);
    }
    updateJwtState(state) {
        this.jwtState$.next(state);
    }
    updateAuthInfo(info) {
        this.authInfoSubject.next(info);
    }
    resetState() {
        this.authInfoSubject.next(getInitialState());
        this.updateJwtState("AUTH_ANGULAR__Ready" /* JWTStateEnum.Ready */);
    }
    refreshToken(accessToken, sessionId, authUrl) {
        const headers = { [this.config.tokenHeaderName]: `${this.config.prefixToken}${accessToken}` };
        const context = new HttpContext().set(AUTH_ANGULAR__SKIP_JWT_ADDING, true);
        const params = {
            sessionId: sessionId,
            appId: this.appId ?? 'midc'
        };
        const baseUrl = new URL(authUrl);
        const apiURL = `${baseUrl.origin}/${this.config.refreshPath}`;
        return this.http.get(apiURL, { headers, context, params });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthorizationInterceptorService, deps: [{ token: i1.HttpClient }, { token: AUTH_ANGULAR__LOGOUT_SERVICE, optional: true }, { token: AUTH_ANGULAR__APP_ID, optional: true }, { token: AUTH_ANGULAR__CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthorizationInterceptorService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthorizationInterceptorService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [AUTH_ANGULAR__LOGOUT_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [AUTH_ANGULAR__APP_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_ANGULAR__CONFIG]
                }] }] });

/*
* AuthAngularService is service that should be injected in your application.
*  You should pass IAuthInfo in updateAuthInfo method to let the library know about which token should be used initially.
* */
class AuthAngularService {
    constructor(authorizationInterceptorService) {
        this.authorizationInterceptorService = authorizationInterceptorService;
        /**
         * The Observable to access authInfo.
         * Can have a different values than initial object, because sessionId and token may be already refreshed
         * */
        this.authInfo$ = this.authorizationInterceptorService.authInfo$;
        /**
         * Emit an event if the auth token can't be refreshed.
         * */
        this.loginRequired$ = this.authorizationInterceptorService.loginRequired$;
    }
    /**
     * The method to pass authInfo inside auth-angular library and allows it to attach token to requests and take care of refreshes
     * */
    updateAuthInfo(authInfo) {
        this.authorizationInterceptorService.updateAuthInfo(authInfo);
        this.authorizationInterceptorService.updateJwtState("AUTH_ANGULAR__Ready" /* JWTStateEnum.Ready */);
    }
    /**
     * Use this method to manually reset the state of the interceptor and service. It can be needed when you are using library in web-component.
     * In the web-components the root-services are not destroyed when web-component is removed from DOM.
     * So, you manually trigger this method in app.component in ngOnDestroy hook.
     * */
    ngOnDestroy() {
        this.authorizationInterceptorService.resetState();
    }
    /**
     * The getter to access current authInfo.
     * Can have a different values than initial object, because sessionId and token may be already refreshed
     * */
    get authInfo() {
        return this.authorizationInterceptorService.authInfo;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthAngularService, deps: [{ token: AuthorizationInterceptorService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthAngularService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthAngularService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AuthorizationInterceptorService }] });

/*
*  Provide context to HTTP requests.
* */
class RequestContextService {
    constructor() {
        this._context = '';
    }
    /**
     * To set the context.
     * */
    set context(value) {
        const jsonStr = JSON.stringify(value);
        this._context = btoa(jsonStr);
    }
    /**
     * The context in base64.
     * */
    get contextHeader() {
        return this._context;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: RequestContextService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: RequestContextService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: RequestContextService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class AuthorizationInterceptor {
    constructor(authorizationInterceptorService, config) {
        this.authorizationInterceptorService = authorizationInterceptorService;
        this.config = config;
    }
    intercept(req, next) {
        if (this.isBearerAuthenticationSupported(req)) {
            return this.makeRequestWithJWT(req, next);
        }
        else {
            return next.handle(req);
        }
    }
    isBearerAuthenticationSupported(req) {
        return (this.authorizationInterceptorService.isJWTSupported &&
            !(req.context.get(AUTH_ANGULAR__SKIP_JWT_ADDING)));
    }
    makeRequestWithJWT(req, next, counter = 0) {
        switch (this.authorizationInterceptorService.jwtState) {
            case "AUTH_ANGULAR__CannotBeRefreshed" /* JWTStateEnum.CannotBeRefreshed */:
                return throwError(() => Error(`The request with url: ${req.url} was canceled because token cannot be refreshed`));
            case "AUTH_ANGULAR__InProcessOfRefresh" /* JWTStateEnum.InProcessOfRefresh */:
                return this.makeRequestWhenRefreshIsDone(req, next, ++counter);
            default: {
                const token = this.authorizationInterceptorService.jwt;
                const clonedRequest = req.clone({
                    headers: req.headers.set(this.config.tokenHeaderName, `${this.config.prefixToken}${token}`)
                });
                return next.handle(clonedRequest).pipe(catchError((error) => {
                    if (error.status === HttpStatusCode.Unauthorized && counter < this.config.countOfRefreshRetries) {
                        return this.handleUnauthorizedError(req, next, token, ++counter) ?? throwError(() => error);
                    }
                    return throwError(() => error);
                }));
            }
        }
    }
    makeRequestWhenRefreshIsDone(req, next, counter) {
        return this.authorizationInterceptorService.jwtState$.pipe(filter(jwtState => jwtState !== "AUTH_ANGULAR__InProcessOfRefresh" /* JWTStateEnum.InProcessOfRefresh */), take(1), switchMap(() => this.makeRequestWithJWT(req, next, counter)));
    }
    refreshAuthInfo() {
        return this.authorizationInterceptorService.refreshAuthInfo().pipe(take(1), tap(() => {
            this.authorizationInterceptorService.updateJwtState("AUTH_ANGULAR__Ready" /* JWTStateEnum.Ready */);
        }), catchError((e) => {
            this.authorizationInterceptorService.updateJwtState("AUTH_ANGULAR__CannotBeRefreshed" /* JWTStateEnum.CannotBeRefreshed */);
            return throwError(() => e);
        }));
    }
    handleUnauthorizedError(req, next, token, counter) {
        const jwtState = this.authorizationInterceptorService.jwtState;
        if (jwtState === "AUTH_ANGULAR__InProcessOfRefresh" /* JWTStateEnum.InProcessOfRefresh */) {
            return this.makeRequestWhenRefreshIsDone(req, next, counter);
        }
        if (jwtState === "AUTH_ANGULAR__Ready" /* JWTStateEnum.Ready */) {
            const hasTokenAlreadyUpdated = token !== this.authorizationInterceptorService.jwt;
            if (hasTokenAlreadyUpdated) {
                return this.makeRequestWhenRefreshIsDone(req, next, counter);
            }
            this.authorizationInterceptorService.updateJwtState("AUTH_ANGULAR__InProcessOfRefresh" /* JWTStateEnum.InProcessOfRefresh */);
            const refresh$ = this.refreshAuthInfo();
            const request$ = this.makeRequestWhenRefreshIsDone(req, next, counter);
            return combineLatest([request$, refresh$]).pipe(map(([event]) => event));
        }
        return;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthorizationInterceptor, deps: [{ token: AuthorizationInterceptorService }, { token: AUTH_ANGULAR__CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthorizationInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: AuthorizationInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AuthorizationInterceptorService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_ANGULAR__CONFIG]
                }] }] });

class ContextProviderInterceptor {
    constructor(contextProviderService) {
        this.contextProviderService = contextProviderService;
    }
    intercept(request, next) {
        if (!!this.contextProviderService.contextHeader && !(request.context.get(REQUEST_CONTEXT__SKIP_ADDING_HEADER))) {
            request = request.clone({
                headers: request.headers.set('X-ITERO-BIZCTX', this.contextProviderService.contextHeader)
            });
        }
        return next.handle(request);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: ContextProviderInterceptor, deps: [{ token: RequestContextService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: ContextProviderInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.2", ngImport: i0, type: ContextProviderInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: RequestContextService }] });

/**
 * This provider contains HTTP_INTERCEPTORS that is required for auth-angular library work
 * */
const provideAuthAngularInterceptor = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthorizationInterceptor,
    multi: true
};
/**
 * These providers contain services that are used by Auth-Angular library
 * */
const provideAuthAngularServices = [
    AuthorizationInterceptorService,
    AuthAngularService,
];
/**
 * Use to provide context to your HTTP requests.
 * */
function provideRequestContext() {
    return [
        RequestContextService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ContextProviderInterceptor,
            multi: true
        }
    ];
}

/*
 * Public API Surface of auth-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_ANGULAR__APP_ID, AUTH_ANGULAR__INTERCEPT_CUSTOM_CONFIG, AUTH_ANGULAR__LOGOUT_SERVICE, AUTH_ANGULAR__SKIP_JWT_ADDING, AuthAngularService, REQUEST_CONTEXT__SKIP_ADDING_HEADER, RequestContextService, provideAuthAngularInterceptor, provideAuthAngularServices, provideRequestContext };
